import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Models
import ICalendarEvent, { ICalendarEventType } from "app/models/CalendarEvent";
// Types
import Reducers from 'app/types/Reducers';
// Async
import {
  createCalendarEvent, deleteCalendarEvent, getCalendarEvent,
  getCalendarEvents, patchCalendarEvent, updateCalendarEvent
} from "./CalendarEventsAsync";
import { deleteCalendarEventType, updateCalendarEventType } from "../CalendarEventTypes/CalendarEventTypesAsync";
// Dayjs
import dayjs, { Dayjs } from "dayjs";
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface IState {
  event: ICalendarEvent | null;
  events: ICalendarEvent[] | null;
  params: {
    start: string;
    end: string;
    typeIds: number[];
    caseId: number | null;
    includeAllEvents: boolean;
  };
  currentDate: Dayjs;
  currentView: string;
};

const initialState:IState = {
  event: null,
  events: null,
  params: {
    start: dayjs().startOf('month').subtract(7, 'day').format('YYYY-MM-DD'),
    end: dayjs().endOf('month').add(7, 'day').format('YYYY-MM-DD'),
    typeIds: [],
    caseId: null,
    includeAllEvents: true
  },
  currentDate: dayjs().utc(),
  currentView: 'dayGridMonth'
};

const slice = createSlice({
  name: Reducers.CalendarEvents,
  initialState,
  reducers: {
    setCurrentDate: (state, action:PayloadAction<Dayjs>) => {
      state.currentDate = dayjs(action.payload).utc();
    },
    setCurrentView: (state, action:PayloadAction<string>) => {
      state.currentView = action.payload;
    },
    // Default
    setInitialField: <IStateKey extends keyof IState>(state: IState, action: PayloadAction<IStateKey>) => {
      state[action.payload] = initialState[action.payload];
    }
  },
  extraReducers(builder) {
    builder.addCase(getCalendarEvents.pending, (state, action:any) => {
      // state.events = null;
      state.params = action.meta.arg;
    })
    builder.addCase(getCalendarEvents.fulfilled, (state, action: PayloadAction<ICalendarEvent[]>) => {
      state.events = action.payload;
    })
    builder.addCase(getCalendarEvent.pending, (state) => {
      state.event = null;
    });
    builder.addCase(getCalendarEvent.fulfilled, (state, action:PayloadAction<ICalendarEvent>) => {
      state.event = action.payload;
    });
    builder.addCase(createCalendarEvent.fulfilled, (state, action:PayloadAction<ICalendarEvent>) => {
      if ( state.events ){
        state.events.push(action.payload);
      }
    });
    builder.addCase(updateCalendarEvent.fulfilled, (state, action:PayloadAction<ICalendarEvent>) => {
      if ( state.events ){
        state.events = state.events.map(event => {
          if ( event.id === action.payload.id ) return action.payload;
          return event;
        })
      }
    });
    builder.addCase(patchCalendarEvent.pending, (state, action) => {
      const { eventId, data } = action.meta.arg;
      
      if ( state.events ){
        state.events = state.events.map(event => {
          if ( event.id === eventId ) return { ...event, ...data, oldVersion: event };
          return event;
        })
      }
    });
    builder.addCase(patchCalendarEvent.fulfilled, (state, action:PayloadAction<ICalendarEvent>) => {
      if ( state.events ){
        state.events = state.events.map(event => {
          if ( event.id === action.payload.id ) return action.payload;
          return event;
        })
      }
    });
    builder.addCase(patchCalendarEvent.rejected, (state, action) => {
      const { eventId } = action.meta.arg;
      
      if ( state.events ){
        state.events = state.events.map((event:any) => {
          if ( event.id === eventId && event.oldVersion ) return event.oldVersion;
          return event;
        })
      }
    });
    builder.addCase(deleteCalendarEvent.fulfilled, (state, action:{ payload:number }) => {
      if ( state.events ){
        state.events = state.events.filter(event => event.id !== action.payload);
      }
    });

    // Synchronize calendar events with event type changes
    // Ensures calendar events reflect updates or deletions to their associated event types
    builder.addCase(updateCalendarEventType.fulfilled, (state, action:PayloadAction<ICalendarEventType>) => {
      if ( state.events ){
        state.events = state.events.map(event => {
          if ( event.type?.id === action.payload.id ) {
            return { ...event, type: { ...action.payload } };
          }
          return event;
        });
      }
    });
    builder.addCase(deleteCalendarEventType.fulfilled, (state, action:PayloadAction<number>) => {
      if ( state.events ){
        state.events = state.events.map(event => {
          if ( event.type?.id === action.payload ) {
            return { ...event, type: undefined };
          }
          return event;
        });
      }
    });
  },
});

export const CalendarEventsActions = slice.actions;

export default slice.reducer;
